"use client"

import { useState, useEffect } from "react"
import { CreditCard, CheckCircle, AlertCircle } from "lucide-react"
import { Button } from "@/components/ui/button"
import { Input } from "@/components/ui/input"
import { Label } from "@/components/ui/label"
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "@/components/ui/card"
import { Alert, AlertDescription } from "@/components/ui/alert"
import axios from "axios"
import { API_ENDPOINTS } from "../../../constants/config"
import { toast } from "sonner"

const initialForm = {
  linkedAccountId: "",
  linkedAccountName: "",
  linkedAccountEmail: "",
  linkedAccountContact: "",
}

export default function RazorpayRouteSettings() {
  const [loading, setLoading] = useState(true)
  const [saving, setSaving] = useState(false)
  const [configured, setConfigured] = useState(false)
  const [route, setRoute] = useState(null)
  const [form, setForm] = useState(initialForm)

  const fetchRoute = async () => {
    try {
      const token = localStorage.getItem("token")
      const res = await axios.get(API_ENDPOINTS.feesRazorpaySchoolRoute, {
        headers: { Authorization: `Bearer ${token}` },
      })
      const data = res.data?.data
      setConfigured(!!data?.configured)
      if (data?.route) {
        setRoute(data.route)
        setForm({
          linkedAccountId: data.route.linkedAccountId ?? "",
          linkedAccountName: data.route.linkedAccountName ?? "",
          linkedAccountEmail: data.route.linkedAccountEmail ?? "",
          linkedAccountContact: data.route.linkedAccountContact ?? "",
        })
      } else {
        setForm(initialForm)
      }
    } catch (err) {
      toast.error(err.response?.data?.message || "Failed to load Razorpay configuration")
    } finally {
      setLoading(false)
    }
  }

  useEffect(() => {
    fetchRoute()
  }, [])

  const handleSubmit = async (e) => {
    e.preventDefault()
    const { linkedAccountId, linkedAccountName, linkedAccountEmail, linkedAccountContact } = form
    if (!linkedAccountId?.trim() || !linkedAccountName?.trim() || !linkedAccountEmail?.trim() || !linkedAccountContact?.trim()) {
      toast.error("All fields are required")
      return
    }
    setSaving(true)
    try {
      const token = localStorage.getItem("token")
      const payload = {
        linkedAccountId: linkedAccountId.trim(),
        linkedAccountName: linkedAccountName.trim(),
        linkedAccountEmail: linkedAccountEmail.trim().toLowerCase(),
        linkedAccountContact: linkedAccountContact.trim(),
      }
      if (configured) {
        await axios.put(API_ENDPOINTS.feesRazorpaySchoolRoute, payload, {
          headers: { Authorization: `Bearer ${token}` },
        })
        toast.success("Razorpay route updated successfully")
      } else {
        await axios.post(API_ENDPOINTS.feesRazorpaySchoolRoute, payload, {
          headers: { Authorization: `Bearer ${token}` },
        })
        toast.success("Razorpay route configured successfully")
      }
      await fetchRoute()
    } catch (err) {
      toast.error(err.response?.data?.message || "Failed to save")
    } finally {
      setSaving(false)
    }
  }

  if (loading) {
    return (
      <div className="p-6 flex items-center justify-center min-h-[200px]">
        <div className="animate-spin rounded-full h-10 w-10 border-2 border-blue-500 border-t-transparent" />
      </div>
    )
  }

  return (
    <div className="p-6 space-y-6">
      <div>
        <h2 className="text-xl font-semibold text-gray-900">Online Payment (Razorpay)</h2>
        <p className="text-sm text-gray-600 mt-1">
          Link your Razorpay Route so students can pay fees via Gateway. Get Linked Account ID from Razorpay Dashboard.
        </p>
      </div>

      {configured && (
        <Alert className="border-green-200 bg-green-50">
          <CheckCircle className="h-4 w-4 text-green-600" />
          <AlertDescription className="text-green-800">
            Razorpay is configured. Gateway payments will be enabled for fee collection.
          </AlertDescription>
        </Alert>
      )}

      {!configured && (
        <Alert className="border-amber-200 bg-amber-50">
          <AlertCircle className="h-4 w-4 text-amber-600" />
          <AlertDescription className="text-amber-800">
            No Razorpay route configured. Students will see &quot;School does not have a configured Razorpay Route&quot; when paying via Gateway.
          </AlertDescription>
        </Alert>
      )}

      <Card>
        <CardHeader>
          <CardTitle className="flex items-center gap-2">
            <CreditCard className="h-5 w-5" />
            {configured ? "Update Razorpay Route" : "Configure Razorpay Route"}
          </CardTitle>
          <CardDescription>
            Enter the Linked Account details from your Razorpay Dashboard (Route / Linked Accounts).
          </CardDescription>
        </CardHeader>
        <CardContent>
          <form onSubmit={handleSubmit} className="space-y-4 max-w-xl">
            <div className="space-y-2">
              <Label htmlFor="linkedAccountId">Linked Account ID</Label>
              <Input
                id="linkedAccountId"
                value={form.linkedAccountId}
                onChange={(e) => setForm((f) => ({ ...f, linkedAccountId: e.target.value }))}
                placeholder="acc_xxxxxxxxxxxx"
                required
              />
            </div>
            <div className="space-y-2">
              <Label htmlFor="linkedAccountName">Account Name</Label>
              <Input
                id="linkedAccountName"
                value={form.linkedAccountName}
                onChange={(e) => setForm((f) => ({ ...f, linkedAccountName: e.target.value }))}
                placeholder="Business or account name"
                required
              />
            </div>
            <div className="space-y-2">
              <Label htmlFor="linkedAccountEmail">Email</Label>
              <Input
                id="linkedAccountEmail"
                type="email"
                value={form.linkedAccountEmail}
                onChange={(e) => setForm((f) => ({ ...f, linkedAccountEmail: e.target.value }))}
                placeholder="account@example.com"
                required
              />
            </div>
            <div className="space-y-2">
              <Label htmlFor="linkedAccountContact">Contact</Label>
              <Input
                id="linkedAccountContact"
                value={form.linkedAccountContact}
                onChange={(e) => setForm((f) => ({ ...f, linkedAccountContact: e.target.value }))}
                placeholder="10-digit mobile number"
                required
              />
            </div>
            <Button type="submit" disabled={saving}>
              {saving ? "Saving..." : configured ? "Update" : "Save"}
            </Button>
          </form>
        </CardContent>
      </Card>
    </div>
  )
}
